import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { track } from '@/lib/analytics'
import { useChannel } from '@/context/ChannelContext'
import { usePollsList } from '@/polls/hooks'
import { bookmarkStep, getOnboardingState } from '@/api/onboarding'
import type { PollListItem, PollStatus } from '@/api/polls'
import { resolveAdsEntryTarget } from '@/onboarding/adsEntry'

/**
 * P1-1b — полная UI-начинка Poll list `/polls` по ТЗ v3.1a §2.1:
 * hero + KPI (4) + Шаблоны (4) + Фильтры (5 pills) + Поиск + Список.
 *
 * Что ещё НЕ подключено (следующие подзадачи):
 * - Lab-membership blocking overlay (P1-1c)
 * - Actions «Скопировать ссылку» / «Меню» на row'ах (пока просто ссылки на detail)
 * - Real backend fetch (после sync-point 28.07 — переключить `USE_MOCKS=false`)
 */

type FilterKey = 'all' | 'live' | 'scheduled' | 'closed' | 'draft'

const TEMPLATES = [
  { id: 'pulse', emoji: '📊', title: 'Pulse-опрос', hint: '«Как вам контент за неделю?»' },
  { id: 'topic', emoji: '🎯', title: 'Тема следующего поста', hint: '«О чём писать дальше?»' },
  { id: 'quiz', emoji: '🧠', title: 'Викторина по теме', hint: 'Quiz с правильным ответом' },
  { id: 'blank', emoji: '✍️', title: 'С чистого листа', hint: 'Свой вопрос и варианты' },
]

const FILTER_STATUS: Record<FilterKey, PollStatus | 'all'> = {
  all: 'all',
  live: 'live',
  scheduled: 'scheduled',
  closed: 'closed',
  draft: 'draft',
}

export function PollsListPage() {
  const [filter, setFilter] = useState<FilterKey>('all')
  const [search, setSearch] = useState('')

  const { setChannelFilter, channelFilter } = useChannel()

  // Audit §7 mitigation (v3.1a §2.1 footnote): при входе на /polls
  // superadmin мог унаследовать `channelFilter='showcase'` из вчерашней
  // Витрина-сессии → ProjectSelector отдаёт узкий список каналов →
  // «Опубликовать в канал» = попадание в чужую showcase-аудиторию.
  // Форсим 'all' один раз при mount. Если юзер вручную сменит — не
  // трогаем (не полностью блокируем свободу).
  useEffect(() => {
    if (channelFilter === 'showcase') {
      setChannelFilter('all')
      track('polls_forced_channel_filter_all', {
        level: 'info',
        wasFilter: channelFilter,
        message: 'channelFilter форсирован с showcase на all при входе на /polls (audit §7)',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, isLoading } = usePollsList(FILTER_STATUS[filter])
  const onboardingStateQuery = useQuery({
    queryKey: ['onboarding-state'],
    queryFn: getOnboardingState,
    staleTime: 60_000,
  })
  const isEmpty = !isLoading && !!data && data.kpi.totalPolls === 0
  const adsPromoTarget = resolveAdsEntryTarget(onboardingStateQuery.data)
  const adsPromoLoading = onboardingStateQuery.isLoading && !onboardingStateQuery.data

  const filteredPolls = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data.polls
    return data.polls.filter((p) => p.question.toLowerCase().includes(q))
  }, [data, search])

  useEffect(() => {
    if (!data) return
    track('polls_list_opened', {
      totalPolls: data.kpi.totalPolls,
      activePolls: data.kpi.activePolls,
      totalUniqueVoters: data.kpi.totalUniqueVoters,
      filter,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.kpi.totalPolls])

  return (
    <div className="polls-list">
      <header className="hero">
        <div>
          <p className="hero-eyebrow">Опросы канала</p>
          <h1 className="hero-title">
            Опросы <em>в канале</em>
          </h1>
          <p className="hero-sub">
            В MAX нет встроенных опросов в каналах — мы публикуем их через нашего бота.
            Голосование <b>в один клик</b>, результаты живо обновляются.
          </p>
        </div>
        <Link to="/polls/new" className="btn-primary" onClick={() => track('poll_create_opened', { from: 'hero-cta' })}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Создать опрос
        </Link>
      </header>

      <div className="kpis">
        <KpiCell label="Всего опросов" value={data?.kpi.totalPolls ?? '—'} sub="за всё время" loading={isLoading} />
        <KpiCell
          label="Активных"
          value={
            <>
              {data?.kpi.activePolls ?? '—'} <em>сейчас</em>
            </>
          }
          sub="идут или запланированы"
          loading={isLoading}
        />
        <KpiCell
          label="Голосующих"
          value={data ? formatNumber(data.kpi.totalUniqueVoters) : '—'}
          sub="уникальных за всё время"
          loading={isLoading}
        />
      </div>

      <Link
        className={`promo-banner ${adsPromoLoading ? 'is-disabled' : ''}`}
        to={adsPromoTarget.href}
        aria-disabled={adsPromoLoading}
        onClick={(e) => {
          if (adsPromoLoading) {
            e.preventDefault()
            return
          }
          track('poll_promo_ads_clicked', {
            from: 'polls-list',
            target: adsPromoTarget.href,
            hasCachedState: !!onboardingStateQuery.data,
          })
          if (adsPromoTarget.bookmarkStep !== undefined) {
            try {
              bookmarkStep(adsPromoTarget.bookmarkStep, adsPromoTarget.bookmarkBranchPath)
            } catch {}
          }
        }}
      >
        <span className="pb-ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 11l18-5v12L3 14v-3z" />
            <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
          </svg>
        </span>
        <span className="pb-txt">
          <span className="pb-h">Запусти поток подписчиков за 10 минут</span>
          <span className="pb-s">Опросы бесплатны. А реклама в MAX через Я.Директ приведёт живых подписчиков в канал.</span>
        </span>
        <span className="pb-btn">
          {adsPromoLoading ? (
            <>
              <span className="pb-spinner" aria-hidden />
              Подготавливаем…
            </>
          ) : (
            'Настроить рекламу →'
          )}
        </span>
      </Link>

      <section className="templates">
        <header className="templates-head">
          <h2 className="templates-title">Шаблоны</h2>
          <p className="templates-sub">Готовые опросы под популярные сценарии</p>
        </header>
        <div className="templates-grid">
          {TEMPLATES.map((t) => (
            <Link
              key={t.id}
              to={`/polls/new?template=${t.id}`}
              className="tpl"
              onClick={() => track('poll_template_clicked', { templateId: t.id })}
            >
              <div className="tpl-emo">{t.emoji}</div>
              <div className="tpl-title">{t.title}</div>
              <div className="tpl-q">{t.hint}</div>
            </Link>
          ))}
        </div>
      </section>

      {!isEmpty && (
      <div className="filter-row">
        <div className="pills" role="tablist">
          <FilterPill k="all" active={filter} onClick={setFilter} label="Все" count={data?.kpi.totalPolls} />
          <FilterPill k="live" active={filter} onClick={setFilter} label="Идут" count={data?.kpi.activePolls} dotColor="var(--tg-blue,#0066ff)" />
          <FilterPill
            k="scheduled"
            active={filter}
            onClick={setFilter}
            label="Запланированы"
            dotColor="#7c3aed"
          />
          <FilterPill k="closed" active={filter} onClick={setFilter} label="Закрыты" dotColor="var(--tg-up,#17a06a)" />
          <FilterPill k="draft" active={filter} onClick={setFilter} label="Черновики" dotColor="#d98e00" />
        </div>
        <div className="filter-spacer" />
        <div className="search-input">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input placeholder="Поиск по вопросу…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      )}

      <div className="list">
        {isLoading && <div className="list-empty">Загружаем список…</div>}
        {!isLoading && data && data.kpi.totalPolls === 0 && !search ? (
          <ZeroState />
        ) : (
          <>
            {!isLoading && filteredPolls.length === 0 && (
              <div className="list-empty">
                {search ? 'Ничего не нашли по этому запросу.' : 'Опросов пока нет — создайте первый по шаблону выше.'}
              </div>
            )}
            {filteredPolls.map((p) => (
              <PollRow key={p.id} poll={p} />
            ))}
          </>
        )}
      </div>

      <PageStyles />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Zero-state — экран «Спросите подписчиков первый раз» когда totalPolls===0.
// Copy: design_v2_pool/polls.html #pollZero (2026-07-28).
// ─────────────────────────────────────────────────────────────────────────────
function ZeroState() {
  const { selectedChannel } = useChannel()
  const createHref = selectedChannel
    ? `/polls/new?channelUUID=${encodeURIComponent(selectedChannel.uuid)}`
    : '/polls/new'
  return (
    <div className="zero" role="status">
      <div className="zero-ic" aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12h4l3 8 4-16 3 8h4" />
        </svg>
      </div>
      <h3 className="zero-h">Спросите подписчиков <em>первый&nbsp;раз</em></h3>
      <p className="zero-p">
        Опрос собирается за минуту и публикуется в канал в один клик. Люди голосуют прямо
        в MAX, вы видите результаты живьём.
      </p>
      <Link className="zero-cta" to={createHref} onClick={() => track('poll_zero_cta_click', { from: 'polls-list' })}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Создать первый опрос
      </Link>
      <span className="zero-alt">
        или возьмите{' '}
        <a href="#tpls" onClick={(e) => { e.preventDefault(); document.querySelector('.templates')?.scrollIntoView({ behavior: 'smooth' }) }}>готовый шаблон</a> выше — вопрос уже написан
      </span>
      <div className="zero-why">
        <div>
          <b>Живой канал</b>
          <span>Опрос даёт всплеск активности — алгоритмы MAX это любят.</span>
        </div>
        <div>
          <b>Понятные темы</b>
          <span>Узнаете, о чём писать, прежде чем тратить время.</span>
        </div>
        <div>
          <b>Сегменты</b>
          <span>Видно, как ответили пришедшие с рекламы и ядро.</span>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Row + KPI cell + FilterPill — вспомогательные компоненты
// ─────────────────────────────────────────────────────────────────────────────

function PollRow({ poll }: { poll: PollListItem }) {
  const isDraft = poll.status === 'draft'
  const badge = statusBadge(poll)
  const rowClass = `row row-${poll.status}`
  // Draft → edit-mode (PATCH через CreatePollPage). Scheduled — тоже не могут
  // редактироваться (B PATCH требует status='draft'), ведём на results.
  const to = isDraft ? `/polls/new?edit=${poll.id}` : `/polls/${poll.id}`
  return (
    <Link to={to} className={rowClass}>
      <div className={`row-icon type-${poll.type}`} title={typeLabel(poll.type)}>
        {typeIcon(poll.type)}
      </div>
      <div className="row-body">
        <h3 className="row-title">{poll.question}</h3>
        <div className="row-meta">
          {poll.publishedAt && (
            <>
              <span>
                Опубликован <b>{formatDate(poll.publishedAt)}</b>
              </span>
              <span className="row-meta-sep" />
            </>
          )}
          <span>{formatMeta(poll)}</span>
        </div>
      </div>
      <span className={`status status-${badge.tone}`}>
        {badge.dot && <span className="pip" />}
        {badge.label}
      </span>
      <div className="stat-cell">
        <span className="stat-cell-v">{poll.totalVoters || '—'}</span>
        <span className="stat-cell-l">{voteLabel(poll)}</span>
      </div>
    </Link>
  )
}

function KpiCell({
  label,
  value,
  sub,
  loading,
}: {
  label: string
  value: React.ReactNode
  sub: React.ReactNode
  loading: boolean
}) {
  return (
    <div className={`kpi ${loading ? 'kpi-loading' : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-sub">{sub}</div>
    </div>
  )
}

function FilterPill({
  k,
  active,
  onClick,
  label,
  count,
  dotColor,
}: {
  k: FilterKey
  active: FilterKey
  onClick: (k: FilterKey) => void
  label: string
  count?: number
  dotColor?: string
}) {
  return (
    <button className={`pill ${active === k ? 'on' : ''}`} type="button" onClick={() => onClick(k)}>
      {dotColor && <span className="pill-dot" style={{ background: dotColor }} />}
      {label}
      {typeof count === 'number' && <span className="pill-count">{count}</span>}
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU')
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const isToday = d.toDateString() === today.toDateString()
  if (isToday) {
    return `сегодня, ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  }
  const daysAgo = Math.floor((today.getTime() - d.getTime()) / 86400000)
  if (daysAgo === 1) return 'вчера'
  if (daysAgo < 7) return `${daysAgo} дн назад`
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
}

function formatMeta(p: PollListItem): string {
  const parts: string[] = []
  parts.push(`${p.optionCount} вариант${p.optionCount === 1 ? '' : p.optionCount < 5 ? 'а' : 'ов'}`)
  if (p.type === 'multi') parts.push('несколько ответов')
  if (p.allowRevote && p.status === 'live') parts.push('можно сменить голос')
  return parts.join(' · ')
}

function typeLabel(t: PollListItem['type']): string {
  return t === 'single' ? 'Один вариант' : t === 'multi' ? 'Несколько ответов' : 'Викторина'
}

function typeIcon(t: PollListItem['type']) {
  if (t === 'quiz') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5M12 16h.01" />
      </svg>
    )
  }
  if (t === 'multi') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.5" fill="currentColor" />
    </svg>
  )
}

function statusBadge(p: PollListItem): { label: string; tone: string; dot: boolean } {
  if (p.status === 'draft') return { label: 'Черновик', tone: 'draft', dot: true }
  if (p.status === 'scheduled') return { label: 'Запланирован', tone: 'scheduled', dot: true }
  if (p.status === 'live') return { label: 'Идёт', tone: 'live', dot: true }
  // closed — учитываем closeReason (v3.1a §2.1 маппинг таблица)
  if (p.closeReason === 'message_deleted') return { label: 'Сообщение удалено', tone: 'warn', dot: false }
  if (p.closeReason === 'bot_deadmin') return { label: 'Бот не админ', tone: 'warn', dot: false }
  return { label: 'Закрыт', tone: 'closed', dot: true }
}

function voteLabel(p: PollListItem): string {
  if (p.status === 'draft') return 'не запущен'
  if (p.status === 'scheduled') return 'ещё не запущен'
  if (p.type === 'quiz') return 'ответов'
  return 'голосов'
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles — inline scoped (по образцу BranchPage/ShowcaseOverviewPage)
// ─────────────────────────────────────────────────────────────────────────────

function PageStyles() {
  return (
    <style>{`
      .polls-list { max-width: 1120px; margin: 0 auto; width: 100%; min-width: 0; box-sizing: border-box; }
      .hero {
        display: grid; grid-template-columns: 1fr auto; gap: 24px;
        align-items: flex-end; margin-bottom: 24px;
      }
      .hero-eyebrow {
        font-weight: 700; font-size: 11px;
        letter-spacing: 0.12em; text-transform: uppercase;
        color: var(--tg-mute, #8f9eb0); margin: 0 0 10px;
      }
      .hero-title {
        font-weight: 800; font-size: 40px; line-height: 1.02;
        letter-spacing: -0.028em; color: var(--tg-ink, #111);
        margin: 0 0 6px;
      }
      .hero-title em {
        font-style: italic; font-weight: 400;
        color: var(--tg-blue, #0066ff); letter-spacing: -0.01em;
      }
      .hero-sub {
        font-size: 14.5px; color: var(--tg-mute, #8f9eb0);
        margin: 0; max-width: 620px; line-height: 1.5;
      }
      .hero-sub b { color: var(--tg-ink-2, #333); font-weight: 600; }
      .btn-primary {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 11px 18px; border-radius: 12px;
        background: var(--tg-blue, #0066ff); color: white;
        font-weight: 600; font-size: 14px; text-decoration: none;
        box-shadow: 0 1px 2px rgba(0,102,255,0.18); white-space: nowrap;
      }
      .btn-primary:hover { background: var(--tg-blue-hover, #0052cc); }
      .btn-primary svg { width: 14px; height: 14px; }

      .kpis {
        display: grid; grid-template-columns: repeat(3, 1fr);
        gap: 16px; margin-bottom: 24px;
      }
      .kpi {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px; padding: 18px 20px;
      }
      .kpi-label {
        font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
        color: var(--tg-mute, #8f9eb0);
        text-transform: uppercase; margin-bottom: 8px;
      }
      .kpi-value {
        font-weight: 800; font-size: 28px; line-height: 1;
        letter-spacing: -0.02em; color: var(--tg-ink, #111);
        font-variant-numeric: tabular-nums;
      }
      .kpi-value em {
        font-style: italic; font-weight: 400;
        color: var(--tg-blue, #0066ff);
        font-size: 0.7em; margin-left: 4px;
      }
      .kpi-sub {
        font-size: 12px; color: var(--tg-mute, #8f9eb0); margin-top: 6px;
      }
      .kpi-sub b { color: var(--tg-up, #17a06a); font-weight: 700; }
      .kpi-loading .kpi-value { color: var(--tg-mute-2, #c4c4c8); }

      .promo-banner {
        display: grid; grid-template-columns: 48px 1fr auto;
        gap: 16px; align-items: center;
        padding: 14px 18px; margin-bottom: 24px;
        background: linear-gradient(90deg, rgba(0,102,255,0.06), rgba(0,102,255,0.02));
        border: 1px solid var(--tg-blue-soft, rgba(0,128,255,0.20));
        border-radius: 14px;
        text-decoration: none; color: inherit;
        transition: all 150ms;
      }
      .promo-banner:hover {
        border-color: var(--tg-blue, #0066ff);
        background: linear-gradient(90deg, rgba(0,102,255,0.10), rgba(0,102,255,0.04));
      }
      .promo-banner.is-disabled {
        pointer-events: none;
        opacity: 0.84;
      }
      .pb-ic {
        width: 48px; height: 48px; border-radius: 12px;
        background: var(--tg-blue-bg, #ebf2ff);
        color: var(--tg-blue-press, #0052cc);
        display: flex; align-items: center; justify-content: center;
      }
      .pb-ic svg { width: 22px; height: 22px; }
      .pb-txt { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
      .pb-h { font-weight: 700; font-size: 14px; color: var(--tg-ink, #111); }
      .pb-s { font-size: 12.5px; color: var(--tg-mute, #8f9eb0); line-height: 1.4; }
      .pb-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 8px 14px; border-radius: 10px;
        background: var(--tg-blue, #0066ff); color: white;
        font-weight: 600; font-size: 13px; white-space: nowrap;
      }
      .pb-spinner {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.45);
        border-top-color: white;
        animation: spin 0.8s linear infinite;
        flex-shrink: 0;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      @media (max-width: 700px) {
        .promo-banner { grid-template-columns: 40px 1fr; }
        .pb-btn { grid-column: span 2; text-align: center; }
      }

      .templates {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px; padding: 18px 22px 20px;
        margin-bottom: 24px;
      }
      .templates-head {
        display: flex; align-items: baseline; gap: 12px; margin-bottom: 14px;
      }
      .templates-title { font-weight: 700; font-size: 15px; color: var(--tg-ink, #111); margin: 0; }
      .templates-sub { font-size: 12.5px; color: var(--tg-mute, #8f9eb0); margin: 0; }
      .templates-grid {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
      }
      @media (max-width: 1000px) { .templates-grid { grid-template-columns: repeat(2, 1fr); } }
      .tpl {
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 12px; padding: 14px;
        background: var(--tg-bg, #fafafd); cursor: pointer;
        text-align: left; text-decoration: none;
        color: inherit; display: block;
        transition: all 150ms;
      }
      .tpl:hover {
        border-color: var(--tg-blue-soft, rgba(0,128,255,0.4));
        background: var(--tg-surface, #fff);
        box-shadow: 0 1px 3px rgba(0,0,0,0.04);
      }
      .tpl-emo { font-size: 22px; margin-bottom: 6px; line-height: 1; }
      .tpl-title { font-weight: 600; font-size: 14px; color: var(--tg-ink, #111); margin-bottom: 4px; }
      .tpl-q { font-size: 12px; color: var(--tg-mute, #8f9eb0); line-height: 1.4; }

      .filter-row {
        display: flex; align-items: center; gap: 10px;
        margin-bottom: 14px; flex-wrap: wrap;
      }
      .pills {
        display: inline-flex; gap: 2px; padding: 3px;
        background: var(--tg-surface-2, #f5f5f9); border-radius: 12px;
        max-width: 100%;
        overflow-x: auto;
        scrollbar-width: none;
      }
      .pills::-webkit-scrollbar { display: none; }
      .pill {
        padding: 7px 14px; border: 0; background: transparent;
        cursor: pointer; font-family: inherit; font-weight: 600;
        font-size: 13px; color: var(--tg-mute, #8f9eb0);
        border-radius: 9px; display: inline-flex; align-items: center; gap: 6px;
      }
      .pill:hover { color: var(--tg-ink-2, #333); }
      .pill.on {
        background: white; color: var(--tg-ink, #111);
        box-shadow: 0 1px 3px rgba(0,0,0,0.06);
      }
      .pill-dot { width: 7px; height: 7px; border-radius: 50%; }
      .pill-count {
        font-size: 11px; font-weight: 700;
        color: var(--tg-mute-2, #c4c4c8);
        font-variant-numeric: tabular-nums;
      }
      .pill.on .pill-count { color: var(--tg-blue, #0066ff); }
      .filter-spacer { flex: 1; }
      .search-input {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 8px 12px; background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 10px; font-size: 13.5px; width: 240px;
        box-sizing: border-box;
      }
      .search-input svg { width: 14px; height: 14px; color: var(--tg-mute, #8f9eb0); }
      .search-input input { border: 0; outline: 0; flex: 1; font: inherit; background: transparent; }

      .list { display: flex; flex-direction: column; gap: 10px; }
      .list-empty {
        padding: 32px; text-align: center;
        color: var(--tg-mute, #8f9eb0);
        background: var(--tg-surface, #fff);
        border: 1px dashed var(--tg-line-2, #f0f0f4);
        border-radius: 12px; font-size: 13.5px;
      }
      .row {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 12px; padding: 16px 20px;
        display: grid; grid-template-columns: auto 1fr auto auto;
        gap: 16px; align-items: center;
        text-decoration: none; color: inherit;
        transition: all 150ms;
      }
      .row-disabled { cursor: not-allowed; opacity: 0.85; }
      .row-disabled:hover { border-color: var(--tg-line-2, #f0f0f4); box-shadow: none; transform: none; }
      .row:hover {
        border-color: var(--tg-line, #e8e8ec);
        box-shadow: 0 2px 6px rgba(0,0,0,0.04);
      }
      .row-live { border-left: 3px solid var(--tg-blue, #0066ff); padding-left: 18px; }
      .row-draft { background: var(--tg-bg, #fafafd); border-style: dashed; }
      .row-closed { opacity: 0.85; }
      .row-icon {
        width: 40px; height: 40px; border-radius: 10px;
        background: var(--tg-surface-2, #f5f5f9);
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .row-icon svg { width: 20px; height: 20px; }
      .type-single { background: rgba(0,128,255,0.08); color: var(--tg-blue, #0066ff); }
      .type-multi { background: rgba(124,58,237,0.08); color: #7c3aed; }
      .type-quiz { background: rgba(0,168,96,0.08); color: var(--tg-up, #17a06a); }
      .row-body { min-width: 0; }
      .row-title {
        font-weight: 600; color: var(--tg-ink, #111);
        font-size: 15px; margin: 0 0 4px; line-height: 1.3;
      }
      .row-meta {
        display: flex; align-items: center; gap: 10px;
        flex-wrap: wrap; font-size: 12px; color: var(--tg-mute, #8f9eb0);
      }
      .row-meta-sep {
        width: 3px; height: 3px; border-radius: 50%;
        background: var(--tg-mute-2, #c4c4c8);
      }
      .row-meta b { color: var(--tg-ink-2, #333); font-weight: 600; font-variant-numeric: tabular-nums; }
      .status {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 10px 4px 8px; border-radius: 999px;
        font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
        text-transform: uppercase;
        background: var(--tg-surface-2, #f5f5f9); color: var(--tg-mute, #8f9eb0);
      }
      .status .pip { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
      .status-live { background: rgba(0,102,255,0.10); color: var(--tg-blue-press, #0052cc); }
      .status-live .pip { animation: pip-pulse 1.4s ease-in-out infinite; }
      @keyframes pip-pulse {
        0%,100% { box-shadow: 0 0 0 0 rgba(0,102,255,0.4); }
        50% { box-shadow: 0 0 0 4px rgba(0,102,255,0); }
      }
      .status-closed { background: rgba(0,168,96,0.10); color: var(--tg-up, #17a06a); }
      .status-scheduled { background: rgba(124,58,237,0.10); color: #7c3aed; }
      .status-draft { background: #fff8e1; color: #8b6100; }
      .status-warn { background: #fff8e1; color: #b8620a; }
      .stat-cell {
        display: flex; flex-direction: column;
        align-items: flex-end; gap: 2px; min-width: 80px;
      }
      .stat-cell-v {
        font-weight: 700; color: var(--tg-ink, #111); font-size: 16px;
        font-variant-numeric: tabular-nums; letter-spacing: -0.01em;
      }
      .stat-cell-l { font-size: 11px; color: var(--tg-mute, #8f9eb0); font-weight: 500; }

      /* Zero-state — design_v2_pool/polls.html #pollZero (2026-07-28) */
      .zero {
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 14px;
        background: var(--tg-surface, #fff);
        padding: 44px 32px 40px;
        text-align: center;
        position: relative;
        overflow: hidden;
      }
      .zero::before {
        content: ""; position: absolute; inset: 0;
        background: radial-gradient(620px 220px at 50% -30%, rgba(0,102,255,0.08), transparent 70%);
        pointer-events: none;
      }
      .zero-ic {
        position: relative; width: 62px; height: 62px; margin: 0 auto 18px;
        border-radius: 18px; color: #fff;
        background: linear-gradient(135deg, var(--tg-blue, #0066ff), var(--tg-blue-press, #0052cc));
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 12px 28px -10px rgba(0,102,255,0.6);
      }
      .zero-ic svg { width: 30px; height: 30px; }
      .zero-h {
        position: relative; font-weight: 800; font-size: 22px; letter-spacing: -0.02em;
        color: var(--tg-ink, #111); margin: 0 0 8px;
      }
      .zero-h em { font-style: italic; font-weight: 400; color: var(--tg-blue, #0066ff); }
      .zero-p {
        position: relative; font-size: 14.5px; line-height: 1.55;
        color: var(--tg-ink-2, #333); max-width: 440px; margin: 0 auto 22px;
      }
      .zero-cta {
        position: relative; display: inline-flex; align-items: center; gap: 9px;
        background: var(--tg-blue, #0066ff); color: #fff;
        font-family: inherit; font-weight: 700; font-size: 15px;
        padding: 13px 22px; border: 0; border-radius: 13px;
        cursor: pointer; text-decoration: none;
        box-shadow: 0 8px 22px -9px rgba(0,102,255,0.6);
      }
      .zero-cta:hover { background: var(--tg-blue-hover, #0052cc); }
      .zero-cta svg { width: 17px; height: 17px; }
      .zero-alt {
        position: relative; display: block; margin-top: 14px;
        font-size: 13px; color: var(--tg-mute, #8f9eb0);
      }
      .zero-alt a { color: var(--tg-blue, #0066ff); font-weight: 600; text-decoration: none; cursor: pointer; }
      .zero-why {
        position: relative; margin: 30px auto 0; max-width: 620px;
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 0;
        border-top: 1px solid var(--tg-line-2, #f0f0f4); padding-top: 22px; text-align: left;
      }
      .zero-why > div { padding: 0 16px; border-left: 1px solid var(--tg-line-2, #f0f0f4); }
      .zero-why > div:first-child { border-left: 0; padding-left: 0; }
      .zero-why b { display: block; font-size: 13.5px; font-weight: 700; color: var(--tg-ink, #111); margin-bottom: 4px; }
      .zero-why span { font-size: 12.5px; line-height: 1.45; color: var(--tg-mute, #8f9eb0); }
      @media (max-width: 700px) {
        .zero-why { grid-template-columns: 1fr; gap: 14px; }
        .zero-why > div { border-left: 0; padding: 12px 0 0; border-top: 1px solid var(--tg-line-2, #f0f0f4); }
        .zero-why > div:first-child { border-top: 0; padding-top: 0; }
      }

      @media (max-width: 900px) {
        .hero { grid-template-columns: 1fr; }
        .hero-title { font-size: 30px; }
        .kpis { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 720px) {
        .polls-list { padding: 0 2px; }
        .hero { gap: 16px; margin-bottom: 20px; }
        .hero-title { font-size: 26px; line-height: 1.06; }
        .hero-sub { font-size: 13.5px; max-width: none; }
        .btn-primary { width: 100%; justify-content: center; box-sizing: border-box; }

        .kpis { grid-template-columns: 1fr; gap: 12px; margin-bottom: 20px; }
        .kpi { padding: 16px; }
        .kpi-value { font-size: 24px; }

        .promo-banner {
          grid-template-columns: 40px 1fr;
          gap: 12px;
          padding: 14px;
          margin-bottom: 20px;
        }
        .pb-ic { width: 40px; height: 40px; border-radius: 10px; }
        .pb-ic svg { width: 18px; height: 18px; }
        .pb-h { font-size: 13.5px; }
        .pb-s { font-size: 12px; }
        .pb-btn { grid-column: 1 / -1; text-align: center; }

        .templates { padding: 16px; margin-bottom: 20px; }
        .templates-head { flex-direction: column; align-items: flex-start; gap: 4px; }
        .templates-grid { gap: 10px; }
        .tpl { padding: 12px; min-width: 0; }
        .tpl-title, .tpl-q { overflow-wrap: anywhere; }

        .filter-row {
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        }
        .filter-spacer { display: none; }
        .pills { width: 100%; box-sizing: border-box; }
        .search-input { display: none; }

        .row {
          grid-template-columns: 40px minmax(0, 1fr);
          gap: 12px;
          padding: 14px 16px;
          align-items: start;
        }
        .row-live { padding-left: 14px; }
        .row-icon {
          width: 36px;
          height: 36px;
          grid-row: 1 / span 3;
        }
        .row-icon svg { width: 18px; height: 18px; }
        .row-body { grid-column: 2; }
        .row-title {
          font-size: 22px;
          line-height: 1.18;
          margin-bottom: 6px;
          overflow-wrap: anywhere;
        }
        .row-meta {
          gap: 6px 10px;
          font-size: 11.5px;
        }
        .status {
          grid-column: 2;
          justify-self: start;
          margin-top: 2px;
        }
        .stat-cell {
          grid-column: 2;
          align-items: flex-start;
          min-width: 0;
        }
      }
      @media (max-width: 560px) {
        .templates-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .row-title { font-size: 18px; }
        .zero { padding: 32px 18px 28px; }
        .zero-p { font-size: 13.5px; }
        .zero-cta {
          width: 100%;
          justify-content: center;
          box-sizing: border-box;
        }
      }
    `}</style>
  )
}
